/**
 * Dev-auth shim — a test seam the Playwright e2e suite is meant to drive,
 * not a replacement for real sign-in.
 *
 * This is deliberately NOT the `createFallbackToken()` that §6 Task 1
 * deleted from the old MSAL implementation. That function minted an
 * unsigned JWT in source, hardcoding a real colleague's identity (a real
 * email, `oid`, `tid`), gated only on `NODE_ENV === 'development'`. Three
 * differences make this module safe where that one was not:
 *
 * 1. MINTS NOTHING. `getDevToken()` only reads whatever token has been
 *    written into `localStorage` under `e2e_dev_token`; this module never
 *    constructs a credential. That is a CONTRACT the e2e harness must
 *    satisfy, not present behaviour of this repo yet —
 *    `tests/e2e/globalSetup.js` is still a stub and writes nothing there
 *    today, so until it does, `getDevToken()` simply returns null.
 * 2. NO IDENTITY HARDCODED, ANYWHERE IN SOURCE. `createFallbackToken`
 *    hardcoded a specific real person's claims in this file; this module
 *    hardcodes none — it trusts whatever `sub`/claims the injected token
 *    carries, subject only to `useAuth.js`'s decode/expiry checks. The
 *    fixture identity (`e2e-platform-admin@e2e.local`, per
 *    `.env.e2e.example`'s `PLATFORM_ADMIN_EMAILS`) is a property of what
 *    the *suite* mints, not an enforcement this module performs — do not
 *    read this file as guaranteeing a synthetic identity by itself.
 * 3. FIXED AT BUILD TIME, NOT RUNTIME-FLIPPABLE. `isDevAuthEnabled()`
 *    compares against `process.env.REACT_APP_ALLOW_DEV_TOKENS`, which CRA
 *    substitutes at `react-scripts build` time — never against this SPA's
 *    OTHER config channel, `window.__APP_CONFIG__` (rendered by
 *    `frontend/docker-entrypoint.sh` from container env, read at runtime
 *    by `authConfig.js`). That is deliberate: were this flag sourced from
 *    `window.__APP_CONFIG__` the way `authConfig.js`'s OIDC settings are,
 *    the bypass would be flippable on a running production container by
 *    anyone able to set its env, with no rebuild. Reading only
 *    `process.env` means no runtime input can ever change the outcome.
 *    Production's Dockerfile bakes in no `REACT_APP_*` build args, so the
 *    flag is unset there: `isDevAuthEnabled()` compares the literal
 *    `undefined` against `'true'`, is `false` on every render, and
 *    `getDevToken()` (and the localStorage read inside it) is never
 *    reached. This is NOT dead-code elimination — the comparison still
 *    runs on every render — it is a comparison that cannot evaluate any
 *    other way, because nothing at runtime can add a
 *    `REACT_APP_ALLOW_DEV_TOKENS` property to the build-time-fixed object
 *    literal CRA bakes into the bundle in its place. Nothing calls
 *    `Object.freeze` on it — unlike the OTHER config channel named above,
 *    `window.__APP_CONFIG__`, which genuinely is frozen
 *    (`frontend/docker-entrypoint.sh:35`). The safety here is that no
 *    runtime reference exists through which the substituted literal
 *    could be reached and mutated, not that mutation of it is blocked.
 *
 * The backend counterpart is `DevTokenValidator`
 * (auth/infrastructure/dev_token_validator.py). Reaching it requires
 * BOTH conditions to hold, not one: the process must be started with
 * `OIDC_PROVIDER=dev` (selected by
 * backend/api/security/token_validator_factory.py — a process on
 * `entra`/`keycloak` refuses an unsigned token even if the flag below is
 * set) AND that provider's own `ALLOW_DEV_TOKENS=true`. The production
 * OIDC providers never accept these suite-issued `.unsigned` tokens.
 */

/**
 * Whether the dev-auth bypass is compiled into this build. Fixed when the
 * bundle was built (`process.env.REACT_APP_ALLOW_DEV_TOKENS`, a CRA
 * build-time substitution) — never sourced from runtime container config,
 * so no value settable on a running deployment can change the answer. See
 * this module's docstring, point 3.
 * @returns {boolean}
 */
export const isDevAuthEnabled = () =>
  process.env.REACT_APP_ALLOW_DEV_TOKENS === 'true';

/**
 * Read the token the e2e harness is expected to have written into
 * localStorage before the SPA loads. Never constructs one — returns
 * exactly what is stored, or null if nothing is there or storage is
 * unavailable (see this module's docstring, point 1).
 * @returns {string|null}
 */
export const getDevToken = () => {
  try {
    return window.localStorage.getItem('e2e_dev_token');
  } catch (err) {
    console.warn('devAuth: localStorage unavailable', err);
    return null;
  }
};
